import { useState } from 'react';
import type { ReopenKind } from '@pqrs/contracts';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Field as FieldUi, FieldContent, FieldDescription, FieldLabel, FieldTitle } from '@xiltrion/ui/field';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Spinner } from '@xiltrion/ui/spinner';
import { Textarea } from '@xiltrion/ui/textarea';
import { Field } from '@/components/ui';
import { useCaseRoute, useReopenCase } from '../api';
import { errorMessage, notify } from '../lib';

const REOPEN_OPTIONS: Array<{ kind: ReopenKind; icon: NombreIcono; title: string; description: string }> = [
  { kind: 'COMPLEMENTARY_RESPONSE', icon: 'mdi:message-plus-outline', title: 'Respuesta complementaria', description: 'La respuesta no cubrió todo lo pedido. Sigue el plazo de la petición original.' },
  { kind: 'OTHER', icon: 'mdi:folder-refresh-outline', title: 'Otro motivo', description: 'Recurso, desacato u otra actuación. Empieza un plazo nuevo.' },
];

/** Reabrir un expediente cerrado (índice electrónico y panel de acciones): respuesta complementaria u otro motivo. */
export function ReopenDialog({ caseId, open, onHide }: { caseId: string; open: boolean; onHide: () => void }) {
  const reopen = useReopenCase(caseId);
  const route = useCaseRoute(caseId, open);
  const [kind, setKind] = useState<ReopenKind>('COMPLEMENTARY_RESPONSE');
  const [reason, setReason] = useState('');
  const [nodeId, setNodeId] = useState<string | null>(null);
  const nodes = route.data?.target?.nodes ?? [];
  const complementary = kind === 'COMPLEMENTARY_RESPONSE';
  // La complementaria entra en el paso de responder salvo que se elija otro.
  const respondNode = nodes.find((n) => n.actionCode === 'RESPOND')?.id ?? null;
  const node = nodeId ?? (complementary ? respondNode : null);
  const valid = reason.trim().length >= 10 && (!complementary || !!node);
  const reset = () => { setKind('COMPLEMENTARY_RESPONSE'); setReason(''); setNodeId(null); reopen.reset(); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onHide(); reset(); } }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Reabrir expediente</DialogTitle>
          <DialogDescription>El índice firmado del cierre no cambia. Se abre un índice complementario que sigue la numeración y el caso vuelve a «En curso».</DialogDescription>
        </DialogHeader>
        <RadioGroup value={kind} onValueChange={(v) => { setKind(v as ReopenKind); setNodeId(null); }} className="grid-cols-1 sm:grid-cols-2 gap-2" aria-label="Motivo de la reapertura">
          {REOPEN_OPTIONS.map((o) => (
            <FieldLabel key={o.kind} htmlFor={`reopen-kind-${o.kind}`} className="cursor-pointer">
              <FieldUi orientation="horizontal">
                <FieldContent><Icono nombre={o.icon} className="text-xl text-primary" aria-hidden /><FieldTitle>{o.title}</FieldTitle><FieldDescription className="text-xs">{o.description}</FieldDescription></FieldContent>
                <RadioGroupItem id={`reopen-kind-${o.kind}`} value={o.kind} />
              </FieldUi>
            </FieldLabel>
          ))}
        </RadioGroup>
        <Field label={<>Motivo <span className="sr-only">obligatorio, al menos 10 caracteres</span></>} htmlFor="reopen-reason" hint="Queda en el índice complementario y en la auditoría.">
          <Textarea id="reopen-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={complementary ? 'Ej. no se respondió el punto 3 de la petición' : 'Ej. recurso de reposición presentado por el ciudadano'} aria-invalid={reason.length > 0 && reason.trim().length < 10} />
        </Field>
        {nodes.length > 0 && (
          <Field label="Reabrir en" htmlFor="reopen-node" hint={complementary ? 'El paso de responder, donde se redacta la respuesta con las plantillas de siempre.' : 'Desde el inicio del flujo o directamente en un paso (p. ej. «Cumplir o impugnar» cuando llega el fallo).'}>
            <SelectorSimple id="reopen-node" value={node} limpiable={!complementary} textoSinValor="Desde el inicio" placeholder={complementary ? 'Elige el paso' : 'Desde el inicio'} options={nodes.map((n) => ({ value: n.id, label: n.name }))} onChange={setNodeId} />
          </Field>
        )}
        {complementary && route.data && !node && <Alert variant="warning"><AlertDescription>El flujo de este tipo de caso no tiene un paso de responder. Elige en qué paso reabrir el expediente.</AlertDescription></Alert>}
        {reopen.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(reopen.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => { onHide(); reset(); }}>Cancelar</Button>
          <Button disabled={!valid || reopen.isPending} onClick={() => reopen.mutate({ reason: reason.trim(), kind, ...(node ? { nodeId: node } : {}) }, { onSuccess: () => { notify('success', complementary ? 'Expediente reabierto para la respuesta complementaria' : 'Expediente reabierto'); reset(); onHide(); } })}>{reopen.isPending ? <Spinner /> : <Icono nombre="mdi:folder-refresh-outline" />} Reabrir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
